import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  Lock, Loader2, ArrowLeft, Download, ExternalLink,
  ClipboardList, Home as HomeIcon, Users, Brain, Sparkles, Compass, LineChart, GraduationCap, Target,
  Sun, Bed, Activity, Stethoscope, FileText, ArrowRight, BookOpen, Layers, Eye, Wrench,
} from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import Reveal from "@/components/Reveal";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SEO from "@/components/SEO";
import PdfViewer from "@/components/PdfViewer";
import { CURRICULA, findCurriculum } from "@/data/curricula";

const STATS = [
  { n: "9", label: "Core frameworks", sub: "13 additional in the library" },
  { n: "10", label: "Levels of care", sub: "NARR I–IV + ASAM 1.0–4.0" },
  { n: "8", label: "Role playbooks", sub: "Reception → Medical" },
  { n: "152", label: "Playbook pages", sub: "Print-ready master doc" },
];

const FRAMEWORKS = [
  { icon: <ClipboardList size={18} />, org: "American Society of Addiction Medicine", name: "ASAM Criteria 4th Ed.", body: "Determines the right level of care and when to move up or down." },
  { icon: <HomeIcon size={18} />, org: "National Alliance for Recovery Residences", name: "NARR Standards", body: "Defines the four levels of recovery housing and the operational, ethical, and staffing standards for each." },
  { icon: <Users size={18} />, org: "Best et al.", name: "SIMOR", body: "Reframes recovery as a social-identity transition from using-group to recovery-group." },
  { icon: <Brain size={18} />, org: "Newen", name: "4E Cognition", body: "Treats mind as a brain-body-environment system, so we intervene in the body and the environment, not only in cognition." },
  { icon: <Sparkles size={18} />, org: "Leamy et al.", name: "CHIME", body: "Names the five recovery processes (Connectedness, Hope, Identity, Meaning, Empowerment) that every intervention should reinforce." },
  { icon: <Compass size={18} />, org: "SAMHSA", name: "ROSC", body: "Organizes services and community supports around the patient's chosen recovery pathway, across levels of care." },
  { icon: <LineChart size={18} />, org: "Miller", name: "Feedback-Informed Treatment (FIT)", body: "Uses ultra-brief outcome (ORS) and alliance (SRS) measures every session to prevent deterioration and dropout." },
  { icon: <Target size={18} />, org: "Fortney", name: "Measurement-Based Care (MBC)", body: "Uses standardized symptom measures (PHQ-9, GAD-7, PCL-5, AUDIT, DAST, WHODAS) to guide treatment decisions." },
  { icon: <GraduationCap size={18} />, org: "Ericsson; Rousmaniere; Miller", name: "Deliberate Practice", body: "A structured method of solo and supervised practice targeting specific weak-signal skills." },
];

const NON_NEGOTIABLES = [
  "ORS and SRS every session — feedback-informed treatment is the floor, not the ceiling.",
  "MBC on admission, weekly, and at every LOC transition (PHQ-9, GAD-7, one substance measure).",
  "Person-centered treatment plans in the patient's own language, mapped to CHIME domains.",
  "Warm handoffs at every LOC transition — a phone or video call between sending and receiving clinicians.",
  "Recovery capital is measured with the ARC or BARC-10 at admission, discharge, and 90 days post.",
  "Alumni engagement is a service line, not a marketing tactic.",
  "Deliberate practice for every clinician, every month — one skill, one recording, one tweak.",
  "Every staff member trained in trauma-informed care and Motivational Interviewing basics.",
  "Documentation is a clinical act — notes are written to move the patient forward.",
  "Nothing is delivered without a cultural humility and health-equity check.",
];

const CURRICULUM_ICONS = {
  "recovery-housing": HomeIcon,
  outpatient: Sun,
  "iop-php": Activity,
  residential: Bed,
  medical: Stethoscope,
};

const MASTER_RESOURCES = [
  { name: "Catalyst Continuum Playbook", detail: "152-page master doc — full framework + operational playbook", file: "/downloads/Catalyst_Continuum_Playbook.pdf" },
  { name: "Reference Card — 19 Frameworks", detail: "Print-ready single-page reference card", file: "/downloads/CatalystReferenceCard_19_frameworks.pdf" },
  { name: "Framework Addendum 2026", detail: "Latest framework updates and appendices", file: "/downloads/Catalyst_Framework_Addendum_2026.pdf" },
  { name: "Clinical Onboarding Playbook", detail: "Staff onboarding guide across all levels of care", file: "/downloads/Catalyst_Clinical_Onboarding_Playbook.pdf" },
  { name: "Patient Handbook", detail: "Patient-facing orientation and expectations", file: "/downloads/Catalyst_Patient_Handbook.pdf" },
];

// ==============================================================
// PORTAL LANDING (index)
// ==============================================================
export default function ContinuumPortal() {
  const { user } = useAuth();
  const [access, setAccess] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    if (user && user !== false) {
      (async () => {
        try {
          const [{ data: a }, { data: p }] = await Promise.all([
            api.get("/me/access"),
            api.get("/me/purchases"),
          ]);
          setAccess(a);
          setPurchases(p.filter((x) => a.purchased_continuum_products?.includes(x.product_id)));
        } catch {
          setAccess({ continuum: false });
        }
      })();
    }
  }, [user]);

  if (user === false) return <Navigate to="/login?redirect=/portal/continuum" replace />;
  if (user === null || access === null) {
    return (
      <SiteLayout branch="continuum">
        <div className="section text-center text-[var(--muted)]">Loading…</div>
      </SiteLayout>
    );
  }

  if (!access.continuum) {
    return (
      <SiteLayout branch="continuum">
        <SEO title="Continuum Portal — Access Required" description="Members-only content." path="/portal/continuum" />
        <section className="section max-w-2xl mx-auto text-center" data-testid="portal-locked">
          <Lock className="mx-auto text-[var(--gold)]" size={48} />
          <h1 className="font-display text-4xl text-[var(--navy)] mt-6">Continuum Portal is member-only.</h1>
          <p className="text-[var(--muted)] mt-4 leading-relaxed">
            The Portal unlocks the full playbook: framework guides, level-of-care operations manuals,
            role playbooks, curricula, and non-negotiables — all downloadable and editable for your team.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            <Link to="/academy?category=Toolkits" className="btn btn-navy" data-testid="portal-shop-cta">
              View Continuum Toolkits
            </Link>
            <Link to="/continuum" className="btn btn-ghost">Learn more</Link>
          </div>
        </section>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout branch="continuum">
      <SEO title="Continuum Portal" description="Members-only Catalyst Continuum playbook and resources." path="/portal/continuum" />

      {/* HERO */}
      <section className="grain relative overflow-hidden" style={{ background: "var(--navy)", color: "white", padding: "8vw 6vw 5vw" }} data-testid="portal-hero">
        <Reveal>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-6 h-6 rounded-full bg-[var(--gold)]" />
            <span className="text-[var(--gold)] text-[11px] tracking-[0.22em] font-bold">MEMBER PORTAL · CATALYST CONTINUUM</span>
          </div>
          <h1 className="font-display text-[clamp(42px,5vw,74px)] leading-[1.04]">
            Welcome, <em className="italic text-[var(--gold)]">{user.name?.split(" ")[0] || "colleague"}.</em>
          </h1>
          <p className="text-neutral-300 text-[17px] leading-[1.75] mt-6 max-w-3xl">
            Your access to the full playbook is active. Below is the same evidence-based operating system
            we implement with our consulting clients — now yours to deploy inside your own organization.
          </p>
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-neutral-400 hover:text-[var(--gold)] text-xs tracking-widest mt-6" data-testid="portal-back-dashboard">
            <ArrowLeft size={14} /> BACK TO DASHBOARD
          </Link>
        </Reveal>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--line-dark)] border border-[var(--line-dark)] mt-14 max-w-5xl" data-testid="portal-stats">
          {STATS.map((s) => (
            <div key={s.label} className="bg-[var(--navy-2)] p-8">
              <div className="font-display text-[48px] leading-none text-[var(--gold)]">{s.n}</div>
              <div className="text-white text-sm font-bold tracking-wider mt-3 uppercase">{s.label}</div>
              <div className="text-neutral-400 text-xs mt-2 leading-relaxed">{s.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* LEVEL-OF-CARE CURRICULA */}
      <section className="section bg-[var(--light)]" id="curricula">
        <div className="max-w-[1300px] mx-auto">
          <Reveal className="mb-10">
            <span className="eyebrow-dark">CURRICULA</span>
            <h2 className="font-display mt-4 text-[clamp(32px,3.6vw,50px)] leading-[1.05] text-[var(--navy)]">
              Level-of-care curricula.
            </h2>
            <p className="text-[var(--muted)] leading-relaxed mt-4 max-w-3xl">
              What runs day-to-day at each level of the Continuum — daily rhythm, weekly rotation, framework tags,
              facilitator assignments, and fidelity checks. Each includes downloadable outline, facilitator guide,
              and participant workbook.
            </p>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="curricula-grid">
            {CURRICULA.map((c) => {
              const Icon = CURRICULUM_ICONS[c.id] || Activity;
              return (
                <Link
                  key={c.id}
                  to={`/portal/continuum/curricula/${c.id}`}
                  className="card-editorial block h-full group"
                  data-testid={`curriculum-card-${c.id}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-11 h-11 border border-[var(--gold)] grid place-items-center text-[var(--gold)]">
                      <Icon size={18} />
                    </div>
                    <span className="text-[10px] tracking-widest bg-[var(--cream)] text-[var(--navy)] px-2 py-1 font-bold">
                      {c.levels}
                    </span>
                  </div>
                  <h3 className="font-display text-2xl text-[var(--navy)] mb-2">{c.segment}</h3>
                  <div className="text-[10px] tracking-widest text-[var(--muted)] mb-4">LOS · {c.los.toUpperCase()}</div>
                  <p className="text-sm text-[var(--ink)] leading-relaxed italic mb-6">"{c.tagline}"</p>
                  <span className="text-link">
                    Open curriculum <ArrowRight size={14} />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PURCHASED PRODUCTS */}
      {purchases.length > 0 && (
        <section className="section bg-[var(--cream)]">
          <div className="max-w-[1250px] mx-auto">
            <Reveal>
              <span className="eyebrow-dark">YOUR CONTINUUM LIBRARY</span>
              <h2 className="font-display text-[clamp(32px,3.6vw,46px)] leading-[1.05] text-[var(--navy)] mt-4 mb-8">
                Downloadable resources included with your purchase.
              </h2>
            </Reveal>
            <div className="grid md:grid-cols-2 gap-4" data-testid="portal-library">
              {purchases.map((p) => (
                <div key={p.session_id} className="bg-white border border-[var(--line)] p-6 flex justify-between items-start gap-4">
                  <div>
                    <div className="text-[10px] tracking-widest text-[var(--gold)] font-bold">{p.product_type.toUpperCase()}</div>
                    <div className="font-display text-xl text-[var(--navy)] mt-2">{p.product_name}</div>
                    <div className="text-xs text-[var(--muted)] mt-1">Purchased {new Date(p.created_at).toLocaleDateString()}</div>
                  </div>
                  <ExternalLink size={16} className="text-[var(--gold)] flex-shrink-0" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* MASTER RESOURCES */}
      <section className="section bg-white" id="resources">
        <div className="max-w-[1300px] mx-auto">
          <Reveal className="mb-10">
            <span className="eyebrow-dark">MASTER RESOURCES</span>
            <h2 className="font-display mt-4 text-[clamp(32px,3.6vw,50px)] leading-[1.05] text-[var(--navy)]">
              The whole playbook, one click away.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-4" data-testid="master-resources">
            {MASTER_RESOURCES.map((r) => (
              <div
                key={r.file}
                className="border border-[var(--line)] bg-[var(--light)] p-6 flex items-start justify-between gap-4"
                data-testid={`master-${r.file.split("/").pop()}`}
              >
                <div className="flex gap-4 min-w-0">
                  <FileText size={24} className="text-[var(--gold)] flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="font-display text-lg text-[var(--navy)]">{r.name}</div>
                    <div className="text-sm text-[var(--muted)] mt-1">{r.detail}</div>
                  </div>
                </div>
                <div className="flex flex-col gap-2 flex-shrink-0">
                  <button
                    onClick={() => setViewer({ url: r.file, title: r.name })}
                    className="inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--navy)] text-[var(--navy)] text-[10px] font-bold tracking-widest hover:bg-[var(--navy)] hover:text-white transition-colors"
                    data-testid={`master-view-${r.file.split("/").pop()}`}
                  >
                    <Eye size={12} /> VIEW
                  </button>
                  <a
                    href={r.file}
                    download
                    className="inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--gold)] text-[var(--gold)] text-[10px] font-bold tracking-widest hover:bg-[var(--gold)] hover:text-[var(--navy)] transition-colors"
                  >
                    <Download size={12} /> PDF
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PORTAL NAV — links to ported pages */}
      <section className="section bg-[var(--cream)]" id="explore">
        <div className="max-w-[1300px] mx-auto">
          <Reveal className="mb-10">
            <span className="eyebrow-dark">EXPLORE THE PLAYBOOK</span>
            <h2 className="font-display mt-4 text-[clamp(32px,3.6vw,50px)] leading-[1.05] text-[var(--navy)]">
              Interactive tools & reference sections.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="portal-nav">
            {[
              { to: "/portal/continuum/tools", label: "Interactive Tools", sub: "PHQ-9, GAD-7, ASAM, MSE, staffing…", icon: Wrench },
              { to: "/portal/continuum/frameworks", label: "9 Frameworks", sub: "Deep dive on each framework", icon: Layers },
              { to: "/portal/continuum/levels-of-care", label: "Levels of Care", sub: "ASAM 1.0 → 4.0 + NARR I–IV", icon: Activity },
              { to: "/portal/continuum/roles", label: "Role Playbooks", sub: "Reception → Medical", icon: Users },
              { to: "/portal/continuum/reference-card", label: "Reference Card", sub: "19 frameworks at a glance", icon: BookOpen },
              { to: "/portal/continuum/positioning", label: "Positioning", sub: "Where we win vs. competition", icon: Target },
              { to: "/portal/continuum/ask-playbook", label: "Ask the Playbook", sub: "Search 152 pages instantly", icon: Sparkles },
            ].map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="card-editorial min-h-[160px] flex flex-col justify-between"
                data-testid={`portal-nav-${n.to.split("/").pop()}`}
              >
                <n.icon size={22} className="text-[var(--gold)]" />
                <div>
                  <div className="font-display text-lg text-[var(--navy)] mt-4">{n.label}</div>
                  <div className="text-xs text-[var(--muted)] mt-1 leading-relaxed">{n.sub}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {viewer && <PdfViewer url={viewer.url} title={viewer.title} onClose={() => setViewer(null)} />}

      {/* FRAMEWORK STACK */}
      <section className="section bg-[var(--light)]" id="frameworks">
        <div className="max-w-[1300px] mx-auto">
          <Reveal className="mb-10">
            <span className="eyebrow-dark">THE FRAMEWORK STACK</span>
            <h2 className="font-display mt-4 text-[clamp(32px,3.6vw,50px)] leading-[1.05] text-[var(--navy)]">
              Nine frameworks, each with a job.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FRAMEWORKS.map((f, i) => (
              <article key={f.name} className="card-editorial h-full">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-[var(--gold)]">{f.icon}</div>
                  <div className="text-[10px] tracking-widest text-[var(--muted)] font-bold">{String(i + 1).padStart(2, "0")} / 09</div>
                </div>
                <div className="text-[10px] tracking-widest text-[var(--muted)] font-bold mb-2 uppercase">{f.org}</div>
                <h3 className="font-display text-2xl text-[var(--navy)] mb-3">{f.name}</h3>
                <p className="text-sm text-[var(--muted)] leading-relaxed">{f.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* NON-NEGOTIABLES */}
      <section className="section bg-[var(--cream)]">
        <div className="max-w-[1250px] mx-auto">
          <Reveal className="mb-10">
            <span className="eyebrow-dark">NON-NEGOTIABLES</span>
            <h2 className="font-display mt-4 text-[clamp(32px,3.6vw,50px)] leading-[1.05] text-[var(--navy)]">
              Regardless of level, role, or state — these are always true.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-x-8">
            {NON_NEGOTIABLES.map((item, i) => (
              <div key={i} className="flex gap-6 py-5 border-b border-[var(--line)]">
                <div className="font-display text-2xl text-[var(--gold)] leading-none min-w-[52px]">{String(i + 1).padStart(2, "0")}</div>
                <p className="text-[var(--ink)] leading-[1.65] text-[15px]">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[var(--navy)] text-white px-[6vw] py-16 text-center">
        <p className="text-neutral-300 text-sm">Questions about implementation? Your Catalyst partnership includes support.</p>
        <Link to="/book" className="btn btn-gold mt-6 inline-flex" data-testid="portal-book-strategy">Book a strategy session</Link>
      </section>
    </SiteLayout>
  );
}

// ==============================================================
// CURRICULUM DETAIL PAGE
// ==============================================================
export function CurriculumDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [access, setAccess] = useState(null);
  const [viewer, setViewer] = useState(null);
  const curriculum = findCurriculum(id);

  useEffect(() => {
    if (user && user !== false) {
      (async () => {
        try {
          const { data } = await api.get("/me/access");
          setAccess(data);
        } catch {
          setAccess({ continuum: false });
        }
      })();
    }
  }, [user]);

  if (user === false) return <Navigate to={`/login?redirect=/portal/continuum/curricula/${id}`} replace />;
  if (user === null || access === null) {
    return (
      <SiteLayout branch="continuum">
        <div className="section text-center text-[var(--muted)]">Loading…</div>
      </SiteLayout>
    );
  }
  if (!access.continuum) return <Navigate to="/portal/continuum" replace />;
  if (!curriculum) {
    return (
      <SiteLayout branch="continuum">
        <div className="section text-center">
          <h1 className="font-display text-3xl text-[var(--navy)]">Curriculum not found.</h1>
          <Link to="/portal/continuum" className="text-link mt-6 inline-flex">Back to portal</Link>
        </div>
      </SiteLayout>
    );
  }

  const Icon = CURRICULUM_ICONS[curriculum.id] || Activity;

  return (
    <SiteLayout branch="continuum">
      <SEO
        title={`${curriculum.segment} Curriculum · Portal`}
        description={curriculum.tagline}
        path={`/portal/continuum/curricula/${curriculum.id}`}
      />

      {/* HEADER */}
      <section className="grain relative overflow-hidden" style={{ background: "var(--navy)", color: "white", padding: "7vw 6vw 4vw" }}>
        <div className="max-w-[1300px] mx-auto">
          <Link to="/portal/continuum#curricula" className="inline-flex items-center gap-2 text-neutral-400 hover:text-[var(--gold)] text-xs tracking-widest mb-8" data-testid="curriculum-back">
            <ArrowLeft size={14} /> BACK TO PORTAL
          </Link>
          <div className="flex items-start justify-between flex-wrap gap-6">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 border border-[var(--gold)] grid place-items-center text-[var(--gold)]">
                  <Icon size={20} />
                </div>
                <span className="text-[10px] tracking-widest bg-[var(--navy-2)] text-[var(--gold)] px-3 py-1 font-bold border border-[var(--gold)]">
                  {curriculum.levels}
                </span>
                <span className="text-[10px] tracking-widest text-neutral-400 font-bold">LOS · {curriculum.los.toUpperCase()}</span>
              </div>
              <h1 className="font-display text-[clamp(38px,4.5vw,68px)] leading-[1.04]" data-testid="curriculum-title">
                {curriculum.segment}
              </h1>
              <p className="font-display italic text-[var(--gold)] text-xl mt-4 leading-snug">"{curriculum.tagline}"</p>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION + DOCUMENTS */}
      <section className="section bg-[var(--light)]">
        <div className="max-w-[1300px] mx-auto grid lg:grid-cols-[1fr_0.8fr] gap-10">
          <Reveal>
            <span className="eyebrow-dark">MISSION</span>
            <h2 className="font-display text-3xl text-[var(--navy)] mt-4 mb-5">What this level does.</h2>
            <p className="text-[var(--ink)] leading-[1.8]">{curriculum.mission}</p>
          </Reveal>

          <Reveal>
            <span className="eyebrow-dark">DOCUMENTS</span>
            <h2 className="font-display text-3xl text-[var(--navy)] mt-4 mb-5">Editable playbook set.</h2>
            <div className="space-y-3" data-testid="curriculum-documents">
              {curriculum.documents.map((d) => (
                <div
                  key={d.slug}
                  className="border border-[var(--line)] bg-white p-5 flex items-start justify-between gap-3"
                  data-testid={`doc-${d.slug}`}
                >
                  <div className="flex gap-3 min-w-0">
                    <BookOpen size={20} className="text-[var(--gold)] flex-shrink-0 mt-1" />
                    <div className="min-w-0">
                      <div className="font-display text-lg text-[var(--navy)]">{d.title}</div>
                      <div className="text-xs text-[var(--muted)] mt-1 leading-relaxed">{d.detail}</div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <button
                      onClick={() => setViewer({ url: d.file, title: d.title })}
                      className="inline-flex items-center gap-1 px-2 py-1 border border-[var(--navy)] text-[var(--navy)] text-[10px] font-bold tracking-widest hover:bg-[var(--navy)] hover:text-white transition-colors"
                      data-testid={`doc-view-${d.slug}`}
                    >
                      <Eye size={11} /> VIEW
                    </button>
                    <a
                      href={d.file}
                      download
                      className="inline-flex items-center gap-1 px-2 py-1 border border-[var(--gold)] text-[var(--gold)] text-[10px] font-bold tracking-widest hover:bg-[var(--gold)] hover:text-[var(--navy)] transition-colors"
                      data-testid={`doc-download-${d.slug}`}
                    >
                      <Download size={11} /> PDF
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* SCHEDULE */}
      <section className="section bg-white">
        <div className="max-w-[1300px] mx-auto">
          <Reveal>
            <span className="eyebrow-dark">DAILY / SESSION RHYTHM</span>
            <h2 className="font-display text-[clamp(28px,3vw,40px)] text-[var(--navy)] mt-4 mb-8">What runs, when.</h2>
          </Reveal>
          <div className="border border-[var(--line)]" data-testid="curriculum-schedule">
            {curriculum.schedule.map((s, i) => (
              <div key={i} className={`grid grid-cols-1 md:grid-cols-[240px_1fr] ${i > 0 ? "border-t border-[var(--line)]" : ""}`}>
                <div className="bg-[var(--cream)] p-5 font-display text-lg text-[var(--navy)]">{s.block}</div>
                <div className="p-5 text-[var(--ink)] leading-relaxed text-sm">{s.detail}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WEEKLY ROTATION */}
      {curriculum.rotation && (
        <section className="section bg-[var(--cream)]">
          <div className="max-w-[1300px] mx-auto">
            <Reveal>
              <span className="eyebrow-dark">WEEKLY ROTATION</span>
              <h2 className="font-display text-[clamp(28px,3vw,40px)] text-[var(--navy)] mt-4 mb-8">Curriculum arc across the length of stay.</h2>
            </Reveal>
            <div className="grid md:grid-cols-2 gap-3" data-testid="curriculum-rotation">
              {curriculum.rotation.map((r, i) => (
                <div key={i} className="border border-[var(--line)] bg-white p-5 flex gap-4">
                  <div className="font-display text-3xl text-[var(--gold)] leading-none min-w-[64px]">W{r.week}</div>
                  <div>
                    <div className="font-display text-lg text-[var(--navy)]">{r.topic}</div>
                    {r.detail && <div className="text-xs text-[var(--muted)] mt-1 leading-relaxed">{r.detail}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FRAMEWORKS */}
      <section className="section bg-white">
        <div className="max-w-[1300px] mx-auto">
          <Reveal>
            <span className="eyebrow-dark">FRAMEWORKS IN PLAY</span>
            <h2 className="font-display text-[clamp(28px,3vw,40px)] text-[var(--navy)] mt-4 mb-8">Each framework has a job here.</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-4" data-testid="curriculum-frameworks">
            {curriculum.frameworks.map((f, i) => (
              <div key={i} className="border-l-2 border-[var(--gold)] pl-5 py-2">
                <div className="font-display text-lg text-[var(--navy)]">{f.name}</div>
                <div className="text-sm text-[var(--muted)] mt-1 leading-relaxed">{f.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FACILITATORS + FIDELITY */}
      <section className="section bg-[var(--light)]">
        <div className="max-w-[1300px] mx-auto grid lg:grid-cols-2 gap-10">
          <Reveal>
            <span className="eyebrow-dark">FACILITATORS</span>
            <h2 className="font-display text-3xl text-[var(--navy)] mt-4 mb-6">Who runs what.</h2>
            <ul className="space-y-3" data-testid="curriculum-facilitators">
              {curriculum.facilitators.map((f, i) => (
                <li key={i} className="flex gap-3 text-[var(--ink)] text-sm leading-relaxed">
                  <Users size={16} className="text-[var(--gold)] flex-shrink-0 mt-1" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal>
            <span className="eyebrow-dark">FIDELITY CHECKS</span>
            <h2 className="font-display text-3xl text-[var(--navy)] mt-4 mb-6">Non-negotiable checks.</h2>
            <ul className="space-y-3" data-testid="curriculum-fidelity">
              {curriculum.fidelity.map((f, i) => (
                <li key={i} className="flex gap-3 text-[var(--ink)] text-sm leading-relaxed">
                  <Layers size={16} className="text-[var(--gold)] flex-shrink-0 mt-1" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ADDITIONAL RESOURCES */}
      <section className="section bg-[var(--cream)]">
        <div className="max-w-[1300px] mx-auto">
          <Reveal>
            <span className="eyebrow-dark">ADDITIONAL RESOURCES</span>
            <h2 className="font-display text-3xl text-[var(--navy)] mt-4 mb-8">Everything else you'll want on your desk.</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-3" data-testid="curriculum-resources">
            {curriculum.resources.map((r) => (
              <a
                key={r.href}
                href={r.href}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white border border-[var(--line)] p-4 flex items-center justify-between hover:bg-[var(--light)] transition-colors group"
              >
                <div className="flex gap-3 items-center">
                  <FileText size={16} className="text-[var(--gold)]" />
                  <span className="text-sm text-[var(--navy)] font-semibold">{r.name}</span>
                </div>
                <Download size={14} className="text-[var(--navy)] group-hover:translate-y-1 transition-transform" />
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[var(--navy)] text-white px-[6vw] py-16 text-center">
        <p className="text-neutral-300 text-sm">Need an in-house implementation of the {curriculum.segment} curriculum?</p>
        <Link to="/book" className="btn btn-gold mt-6 inline-flex">Book a strategy session</Link>
      </section>

      {viewer && <PdfViewer url={viewer.url} title={viewer.title} onClose={() => setViewer(null)} />}
    </SiteLayout>
  );
}
